/**
 * Quake-style `.map` brush export (TrenchBroom / Radiant compatible).
 *
 * Each brush is the intersection of six lower half-spaces, and each plane is
 * written as an ordered point triple. A single reversed triple empties or
 * inverts the brush, so the ordering comes from one place — `mesh.brushPlanes`
 * — and every brush is half-space tested before it is written.
 */

import { brushContains, brushPlanes, boxCenter } from '../core/mesh.ts';
import type { CompiledLevel, Solid, SolidRole } from '../core/types.ts';

/** Quake units are 1/32 m by convention here (32 units per meter). */
const UNITS_PER_METER = 32;

const TEXTURE: Record<SolidRole, string> = {
  static_hard: 'GREY_WALL',
  exterior_wall: 'BRICK_EXT',
  soft_panel: 'DRYWALL_SOFT',
  reinforcement_slot: 'METAL_REINF',
  glass: 'GLASS',
  window_barricade: 'WOOD_BARRICADE',
  door_panel: 'WOOD_DOOR',
  lintel: 'GREY_WALL',
  sill: 'GREY_WALL',
  floor: 'CONCRETE_FLOOR',
  roof: 'CONCRETE_ROOF',
  stair: 'CONCRETE_STEP',
  ramp: 'CONCRETE_STEP',
  ladder: 'METAL_LADDER',
  hatch: 'WOOD_HATCH',
  cover: 'CRATE',
  riser: 'CONCRETE_RISER',
};

export interface BrushExportResult {
  text: string;
  brushes: number;
  halfspaceTestsPassed: number;
}

export function toQuakeMap(level: CompiledLevel, solids?: Solid[]): BrushExportResult {
  const list = solids ?? level.solids;
  const u = (v: number): number => Math.round(v * UNITS_PER_METER * 100) / 100;
  const out: string[] = [];
  let passed = 0;

  out.push('// Generated by the LevelSpec compiler — do not hand-edit.');
  out.push(`// level: ${level.spec.id}  (${level.spec.name})`);
  out.push('{');
  out.push('"classname" "worldspawn"');
  out.push(`"message" "${level.spec.name}"`);
  out.push(`"_levelspec_id" "${level.spec.id}"`);

  for (const s of list) {
    if (s.dynamic) continue; // dynamic surfaces are separate entities, never worldspawn
    if (brushContains(s.box, boxCenter(s.box))) passed++;
    out.push(`// ${s.id} [${s.role}]`);
    out.push('{');
    for (const plane of brushPlanes(s.box)) {
      const [a, b, c] = plane.pts;
      out.push(
        `( ${u(a[0])} ${u(a[1])} ${u(a[2])} ) ` +
          `( ${u(b[0])} ${u(b[1])} ${u(b[2])} ) ` +
          `( ${u(c[0])} ${u(c[1])} ${u(c[2])} ) ` +
          `${TEXTURE[s.role]} 0 0 0 1 1`,
      );
    }
    out.push('}');
  }
  out.push('}');

  // Dynamic surfaces become their own brush entities with stable ids, so the
  // static shell is never fused with anything that can change at runtime.
  for (const s of list) {
    if (!s.dynamic) continue;
    if (brushContains(s.box, boxCenter(s.box))) passed++;
    out.push('{');
    out.push(`"classname" "func_${s.role}"`);
    out.push(`"targetname" "${s.id}"`);
    out.push(`"_boundary" "${s.boundary ?? ''}"`);
    out.push(`"_spaces" "${s.spaces.join(' ')}"`);
    out.push('{');
    for (const plane of brushPlanes(s.box)) {
      const [a, b, c] = plane.pts;
      out.push(
        `( ${u(a[0])} ${u(a[1])} ${u(a[2])} ) ` +
          `( ${u(b[0])} ${u(b[1])} ${u(b[2])} ) ` +
          `( ${u(c[0])} ${u(c[1])} ${u(c[2])} ) ` +
          `${TEXTURE[s.role]} 0 0 0 1 1`,
      );
    }
    out.push('}');
    out.push('}');
  }

  for (const m of level.markers) {
    out.push('{');
    out.push(`"classname" "info_${m.kind}"`);
    out.push(`"targetname" "${m.id}"`);
    out.push(`"origin" "${u(m.pos[0])} ${u(m.pos[1])} ${u(m.pos[2] + 0.1)}"`);
    out.push('}');
  }

  return { text: out.join('\n') + '\n', brushes: list.length, halfspaceTestsPassed: passed };
}
